/** The end of the account, and exactly what "delete" means here.
 *
 * Deletion in this service is not a row disappearing, and saying so plainly
 * is the whole job of this component. Four things happen and the reader is
 * told all four before pressing anything: the tokens are revoked, so every
 * installation stops uploading; the identities are anonymised but their rows
 * stay, because those rows are what refuse a second binding of the same
 * provider account; the source ids stay bound, so nobody can re-claim them
 * and start corroborating with themselves; and merged survey data stays in
 * the repo, because other people's surveys have been merged against it and
 * withdrawing it would rewrite the map.
 *
 * The button is a secondary one tinted red rather than a filled red one. The
 * consequence lives in the dialog, and a row of destructive colour at the
 * bottom of a settings page is louder than the action deserves until it has
 * been asked for.
 */

import { useCallback, useState } from 'react';
import { ConfirmDialog } from '@/components/profile/ConfirmDialog';

export interface DeleteAccountRowProps {
  /** Performs the deletion. Throwing leaves the dialog open with the reason. */
  onConfirm: () => Promise<void>;
}

export function DeleteAccountRow({ onConfirm }: DeleteAccountRowProps) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const close = useCallback(() => {
    setAsking(false);
    setError(null);
  }, []);

  const confirm = useCallback(() => {
    setBusy(true);
    setError(null);
    void onConfirm()
      .catch((thrown: unknown) => setError(thrown))
      .finally(() => setBusy(false));
  }, [onConfirm]);

  return (
    <section
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        padding: '14px 16px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-divider)',
      }}
    >
      <div style={{ flex: 1, minWidth: 260 }}>
        <div style={{ fontSize: 13.5 }}>Delete account</div>
        <div style={{ fontSize: 11.5, color: 'var(--color-neutral-600)', textWrap: 'pretty' }}>
          Revokes tokens and anonymises your identities. Source ids stay bound so nobody can
          re-claim them; merged track data stays in the repo.
        </div>
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        style={{
          fontSize: 12.5,
          color: 'oklch(0.72 0.16 20)',
          borderColor: 'oklch(0.45 0.1 20)',
        }}
        onClick={() => setAsking(true)}
      >
        Delete account…
      </button>

      {asking ? (
        <ConfirmDialog
          title="Delete this account?"
          confirmLabel="Delete account"
          onConfirm={confirm}
          onCancel={close}
          busy={busy}
          error={error}
        >
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 4 }}>
            <li>Every token is revoked, so your installations stop uploading at once.</li>
            <li>
              Your source ids stay bound to this account, so they cannot be re-claimed by anyone
              else.
            </li>
            <li>Survey data that has already been merged stays in the track-data repo.</li>
            <li>
              Your sign-in identities are anonymised, and your stored sessions are handed to the
              retention sweep.
            </li>
          </ul>
          <p style={{ margin: '10px 0 0' }}>This cannot be undone.</p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

export default DeleteAccountRow;
